import { MessageSquare, Phone, Mail, FileText, RefreshCw, PlusCircle } from 'lucide-react';
import type { LeadActivity, ActivityType } from '../../types/crm';

interface ActivityListProps {
  activities: LeadActivity[];
  isLoading?: boolean;
}

const ACTIVITY_ICONS: Record<ActivityType, React.ReactNode> = {
  NOTE: <MessageSquare className="w-4 h-4" />,
  CALL: <Phone className="w-4 h-4" />,
  MESSAGE: <Mail className="w-4 h-4" />,
  FORM_SUBMISSION: <FileText className="w-4 h-4" />,
  STATUS_CHANGE: <RefreshCw className="w-4 h-4" />,
  CREATED: <PlusCircle className="w-4 h-4" />,
};

const ACTIVITY_STYLES: Record<ActivityType, string> = {
  NOTE: 'bg-blue-50 text-blue-600',
  CALL: 'bg-green-50 text-green-600',
  MESSAGE: 'bg-amber-50 text-amber-600',
  FORM_SUBMISSION: 'bg-cyan-50 text-cyan-600',
  STATUS_CHANGE: 'bg-rose-50 text-rose-600',
  CREATED: 'bg-teal-50 text-teal-600',
};

const ACTIVITY_LABELS: Record<ActivityType, string> = {
  NOTE: 'Notiz',
  CALL: 'Anruf',
  MESSAGE: 'Nachricht',
  FORM_SUBMISSION: 'Formular',
  STATUS_CHANGE: 'Status',
  CREATED: 'Erstellt',
};

function formatDateTime(dateStr: string): { relative: string; full: string } {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  let relative: string;
  if (diffMins < 1) relative = 'Gerade eben';
  else if (diffMins < 60) relative = `vor ${diffMins} Min.`;
  else if (diffHours < 24) relative = `vor ${diffHours} Std.`;
  else if (diffDays === 1) relative = 'Gestern';
  else if (diffDays < 7) relative = `vor ${diffDays} Tagen`;
  else relative = date.toLocaleDateString('de-DE');

  const full = date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return { relative, full };
}

export function ActivityList({ activities, isLoading }: ActivityListProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-teal-500 border-t-transparent" />
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Noch keine Aktivitäten vorhanden
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity) => {
        const time = formatDateTime(activity.created_at);
        return (
          <div key={activity.id} className="flex gap-3">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${ACTIVITY_STYLES[activity.type]}`}
            >
              {ACTIVITY_ICONS[activity.type]}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={`text-xs font-medium px-2 py-0.5 rounded ${ACTIVITY_STYLES[activity.type]}`}
                >
                  {ACTIVITY_LABELS[activity.type]}
                </span>
                <span className="text-xs text-gray-400" title={time.full}>
                  {time.relative}
                </span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">
                {activity.content}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
